// Helpers for the project/room type-ahead picker used across the e-mail
// classification UI. Focus shows a short browse list; typing filters it. When a
// typed name is *very close* (but not identical) to an existing project we ask
// the user to re-confirm rather than silently creating a near-duplicate room.

/** Normalize a project name for comparison: lowercase, collapse non-alphanumerics. */
export function normalizeName(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function projectSearchKeys(building) {
  const keys = [normalizeName(building && building.name), normalizeName(building && building.projectNo)];
  for (const alias of (building && building.aliases) || []) keys.push(normalizeName(alias));
  return keys.filter(Boolean);
}

/** Case/whitespace-insensitive exact match, or null. */
export function findExactProject(name, buildings) {
  const n = normalizeName(name);
  if (!n) return null;
  return (buildings || []).find((b) => projectSearchKeys(b).includes(n)) || null;
}

/**
 * Active rooms to show when the picker is focused with an empty query, so the
 * user can pick a room without already knowing its name.
 */
export function browseProjects(buildings, limit = 12) {
  return [...(buildings || [])]
    .filter((b) => b && b.id && String(b.name || "").trim())
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
    .slice(0, limit);
}

/**
 * Projects whose name / project number / alias contains the query, ranked so
 * prefix matches come first. Returns [] for an empty query.
 */
export function filterProjects(query, buildings, limit = 8) {
  const q = normalizeName(query);
  if (!q) return [];
  const scored = [];
  for (const b of buildings || []) {
    const keys = projectSearchKeys(b);
    if (!keys.length) continue;
    let best = null;
    for (const name of keys) {
      const idx = name.indexOf(q);
      if (idx === -1) continue;
      const rank = name === q ? 0 : idx === 0 ? 1 : 2;
      if (
        !best
        || rank < best.rank
        || (rank === best.rank && (idx < best.idx || (idx === best.idx && name.length < best.len)))
      ) {
        best = { rank, idx, len: name.length };
      }
    }
    if (best) scored.push({ b, ...best });
  }
  scored.sort((a, b) => a.rank - b.rank || a.idx - b.idx || a.len - b.len);
  return scored.slice(0, limit).map((s) => s.b);
}

/** Levenshtein edit distance between two strings. */
function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/** Normalized similarity in [0,1] (1 = identical). */
export function similarity(a, b) {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na && !nb) return 1;
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  const dist = levenshtein(na, nb);
  return 1 - dist / Math.max(na.length, nb.length);
}

/**
 * Find an existing project that is "very close" (but not identical) to the
 * typed name, so we can prompt "did you mean …?" before creating a new one.
 * Returns the closest building above the threshold, or null.
 */
export function findCloseProject(name, buildings, threshold = 0.75) {
  const n = normalizeName(name);
  if (!n) return null;
  if (findExactProject(name, buildings)) return null; // exact handled elsewhere
  let best = null;
  let bestScore = 0;
  for (const b of buildings || []) {
    const bn = normalizeName(b.name);
    if (!bn) continue;
    let score = similarity(n, bn);
    // Treat "one name contains the other" as very close too (e.g. "Neubau" vs
    // "Neubau LABC"), which pure edit-distance would score lower.
    if (bn.includes(n) || n.includes(bn)) {
      score = Math.max(score, 0.85);
    }
    if (score > bestScore) {
      bestScore = score;
      best = b;
    }
  }
  return bestScore >= threshold ? best : null;
}

/**
 * Suggest a project when its name (or project number) literally appears in the
 * email subject/body. Mirrors EmailClassificationService.matchProjectByContent
 * so compose suggestions stay consistent with inbound classification.
 * Returns the best matching building object, or null.
 */
function editDistance(a, b, max) {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > max) return max + 1;
  const d = Array.from({ length: al + 1 }, () => new Array(bl + 1).fill(0));
  for (let i = 0; i <= al; i++) d[i][0] = i;
  for (let j = 0; j <= bl; j++) d[0][j] = j;
  for (let i = 1; i <= al; i++) {
    let rowMin = d[i][0];
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
      if (d[i][j] < rowMin) rowMin = d[i][j];
    }
    if (rowMin > max) return max + 1;
  }
  return d[al][bl];
}

export function matchProjectFromText(subject, body, buildings) {
  const normalize = (s) =>
    String(s || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
      .replace(/projektview/g, "projectview")
      .replace(/\bprojekt\b/g, "project")
      .replace(/[^a-z0-9äöüß]+/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  const compact = (s) => normalize(s).replace(/\s+/g, "");

  const haystack = normalize(`${subject || ""} ${body || ""}`);
  if (!haystack) return null;
  const padded = ` ${haystack} `;
  const compactHay = compact(haystack);
  const tokens = haystack.split(/\s+/).filter(Boolean);

  const locate = (raw) => {
    const name = normalize(raw);
    if (name.length < 3) return -1;
    const tlds = new Set(["de", "com", "net", "org", "io", "eu", "at", "ch", "uk", "co"]);
    const nextTok = (from, len) => (padded.slice(from + 1 + len).trim().split(/\s+/)[0] || "");
    const exact = padded.indexOf(` ${name} `);
    if (exact !== -1 && !tlds.has(nextTok(exact, name.length).toLowerCase())) return exact;
    const c = compact(raw);
    const isDomainWindow = (start, w) => tlds.has(String(tokens[start + w] || "").toLowerCase());
    if (c.length >= 8 && compactHay.includes(c)) {
      const win = Math.max(1, name.split(/\s+/).filter(Boolean).length);
      let domainOnly = false;
      for (let w = Math.max(1, win - 1); w <= win + 1; w++) {
        for (let i = 0; i <= tokens.length - w; i++) {
          if (tokens.slice(i, i + w).join("") === c && isDomainWindow(i, w)) domainOnly = true;
        }
      }
      if (!domainOnly) return compactHay.indexOf(c);
    }
    if (c.length < 6) return -1;
    const max = c.length < 14 ? 1 : 2;
    const win = Math.max(1, name.split(/\s+/).filter(Boolean).length);
    for (let w = Math.max(1, win - 1); w <= win + 1; w++) {
      for (let i = 0; i <= tokens.length - w; i++) {
        if (isDomainWindow(i, w)) continue;
        const slice = tokens.slice(i, i + w).join("");
        if (slice.length >= 6 && editDistance(slice, c, max) <= max) return i;
      }
    }
    return -1;
  };

  let best = null; // { b, index, len }
  for (const b of buildings || []) {
    let index = locate(b.name);
    let len = compact(b.name).length;
    if (index === -1 && b.projectNo) {
      index = locate(b.projectNo);
      len = compact(b.projectNo).length;
    }
    if (index === -1) continue;
    if (!best || index < best.index || (index === best.index && len > best.len)) {
      best = { b, index, len };
    }
  }
  return best ? best.b : null;
}
